import { gql } from '@apollo/client';
import {
	VehicleViewModel
} from '@/graphql/generated';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { GenericAutocomplete, AutocompleteConfig } from './generic-autocomplete';
import { VehicleCreateForm } from './vehicle-create-form';

export interface VehicleAutocompleteProps {
	value: string;
	onChange: (value: string, vehicle: VehicleViewModel | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	enableCreateNew?: boolean;
	initialSelectedLabel?: string; // Display label to show when form loads with a selected value
	openDelay?: number;
	autoFocus?: boolean; // Automatically focus the input on mount
}

// GraphQL query for vehicles
const VEHICLES_QUERY = gql`
	query VehiclesList(
		$where: VehicleViewModelFilterInput
		$first: Int
		$after: String
		$order: [VehicleViewModelSortInput!]
	) {
		vehicles(where: $where, first: $first, after: $after, order: $order) {
			edges {
				node {
					vehicleId
					plateNumber
					make
					model
					color
					vehicleType {
						vehicleTypeId
						name
					}
					isActive
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

// Helper functions for vehicle display
export const getVehicleDisplayName = (vehicle: VehicleViewModel): string => {
	return vehicle.plateNumber || 'Unknown Plate';
};

export const getVehicleSecondaryText = (vehicle: VehicleViewModel): string => {
	// Show make, model, and color as secondary text
	const parts = [];

	if (vehicle.make && vehicle.model) {
		parts.push(`${vehicle.make} ${vehicle.model}`);
	} else if (vehicle.make) {
		parts.push(vehicle.make);
	} else if (vehicle.model) {
		parts.push(vehicle.model);
	}

	if (vehicle.color) {
		parts.push(vehicle.color);
	}

	if (vehicle.vehicleType?.name) {
		parts.push(`Type: ${vehicle.vehicleType.name}`);
	}

	return parts.join(' • ');
};

// Create vehicle autocomplete configuration
const createVehicleAutocompleteConfig = (enableCreateNew = false): AutocompleteConfig<VehicleViewModel> => ({
	query: VEHICLES_QUERY,
	queryName: 'vehicles',
	searchFields: ['plateNumber', 'make', 'model'],
	orderBy: [{ plateNumber: 'ASC' }],
	getDisplayName: getVehicleDisplayName,
	getSecondaryText: getVehicleSecondaryText,
	getId: (vehicle) => vehicle.vehicleId,
	pageSize: DEFAULT_PAGE_SIZE,
	// Enable details functionality
	enableDetails: true,
	entityType: 'vehicles',
	createNew: enableCreateNew ? {
		enabled: true,
		entityName: 'Vehicle',
		createFormComponent: VehicleCreateForm,
	} : undefined,
});

export const VehicleAutocomplete: React.FC<VehicleAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Search vehicles...',
	disabled = false,
	className = '',
	enableCreateNew = false,
	initialSelectedLabel,
	openDelay = 0,
	autoFocus = false
}) => {
	const config = createVehicleAutocompleteConfig(enableCreateNew);

	return (
		<GenericAutocomplete<VehicleViewModel>
			config={config}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			disabled={disabled}
			className={className}
			initialSelectedLabel={initialSelectedLabel}
			openDelay={openDelay}
			autoFocus={autoFocus}
		/>
	);
};
